// ─────────────────────────────────────────────────────────────────────────
// Element comment-picker — in-page bridge (client half).
//
// Lets a learner in Studio click something in the live preview and have
// that exact element carried along with their next chat instruction,
// without ever exposing DOM/CSS concepts in the UI. Same cross-frame
// pattern as the Ask-AI bridge (see lib/askAI.js): the preview iframe is
// `sandbox="allow-scripts"` with no allow-same-origin, so the parent can
// never reach into its DOM directly (iframe.contentDocument throws). The
// picker has to live INSIDE the generated page as an injected script and
// report back over postMessage — it can't be implemented from the parent
// side the way a real DevTools inspector is.
//
//   generated page  --postMessage(pick)-->  parent (studio.js)
//   parent           --postMessage(arm/disarm)-->  generated page
//
// What the injected script computes on click and sends back:
//   - `locator`: a best-effort CSS selector path to the clicked element.
//     This is NEVER shown to the user — studio.js only uses it internally,
//     folded into the text sent to the generation model, so the model can
//     find the exact element in "CURRENT FILE" instead of guessing from a
//     plain-English description.
//   - `label`: a short, human-readable description ("button reading 'Learn
//     more'") generated from the element's own tag/text — THIS is what the
//     user sees, as a pin chip, never the selector.
//   - `rect`: the clicked element's getBoundingClientRect() within the
//     iframe's own viewport. Combined with the iframe element's position in
//     the PARENT page (which the parent can read fine — iframe.getBoundingClientRect()
//     is just a normal element in the parent's own DOM), this is enough for
//     studio.js to draw a numbered pin marker in the right spot without
//     ever touching the iframe's content itself.
//   - `point`: the exact click position (same viewport coord space as
//     `rect`), used instead of the element's bounding-box center so the pin
//     marker lands where the learner actually clicked.
//   - `canvasPoint`: when the clicked element is a <canvas>, the click
//     position as a {x,y} FRACTION of the canvas's own width/height (not
//     raw pixels, which mean nothing without the canvas's dimensions). Kept
//     as a fallback signal — see `canvasHit` below for the primary one.
//   - `canvasHit`: the real answer to "what was drawn there", computed
//     WITHOUT any cooperation from the kit that drew it. A <canvas> has no
//     DOM to hit-test — so this file monkey-patches CanvasRenderingContext2D
//     itself (installCanvasInstrumentation, below) to transparently log every
//     shape/text/image any script on the page paints: its bounding box in
//     device pixels, the exact arguments it was drawn with (so the model can
//     grep CURRENT FILE for those numbers instead of reverse-engineering
//     position math), and the live fillStyle/strokeStyle/font active at
//     paint time. Logging is unconditional from page load (not gated to
//     when picking is armed) specifically so a STATIC diagram — drawn once
//     and never redrawn — still has something to hit-test; each canvas's log
//     is capped so a busy animation can't grow it unbounded. On click, the
//     log is walked newest-first (newest wins on overlap — that's always
//     the current visual truth for anything redrawn since) to find every
//     logged shape under the point, plus any nearby text, plus (as an
//     always-available last resort) the raw pixel color at that point.
// ─────────────────────────────────────────────────────────────────────────

const PICKER_SCRIPT_MARKER = 'window.__llElementPickerBridgeInstalled';

const PICKER_SCRIPT = `<script>
(function () {
  if (window.__llElementPickerBridgeInstalled) return;
  window.__llElementPickerBridgeInstalled = true;
  var armed = false;
  var hoverBox = document.createElement('div');
  hoverBox.setAttribute('data-ll-bridge-ui', 'picker');
  hoverBox.style.cssText = 'position:fixed;pointer-events:none;z-index:2147483647;'
    + 'border:2px solid #cc785c;background:rgba(204,120,92,0.15);border-radius:4px;display:none;';
  function attachHoverBox() {
    if (document.documentElement && !document.documentElement.contains(hoverBox)) {
      document.documentElement.appendChild(hoverBox);
    }
  }
  attachHoverBox();
  document.addEventListener('DOMContentLoaded', attachHoverBox);

  var TYPE_LABELS = {
    button: 'button', a: 'link', img: 'image', input: 'input field', textarea: 'text box',
    select: 'dropdown', h1: 'heading', h2: 'heading', h3: 'heading', h4: 'heading',
    p: 'paragraph', canvas: 'canvas', svg: 'graphic', label: 'label', li: 'list item',
    td: 'table cell', th: 'table header'
  };

  function describeElement(el) {
    var tag = (el.tagName || '').toLowerCase();
    var friendly = TYPE_LABELS[tag] || 'element';
    var raw = (el.innerText || el.value || el.getAttribute('alt') || el.getAttribute('aria-label') || '').trim();
    var text = raw.replace(/\\s+/g, ' ').slice(0, 40);
    return text ? (friendly + ' reading "' + text + (raw.length > 40 ? '...' : '') + '"') : friendly;
  }

  function choosePickElement(el) {
    if (!el || el.nodeType !== 1 || el.closest('[data-ll-bridge-ui]')) return null;
    var directSelector = 'button,a,input,textarea,select,canvas,svg,img,video,iframe,[role="button"],[role="link"],[role="img"],[role="figure"]';
    var direct = el.closest(directSelector);
    if (direct && direct !== document.documentElement && direct !== document.body) return direct;
    var node = el;
    while (node && node.nodeType === 1 && node !== document.body && node !== document.documentElement) {
      var cls = node.classList;
      var tag = (node.tagName || '').toLowerCase();
      if (
        (cls && (cls.contains('ll-card') || cls.contains('card') || cls.contains('panel') || cls.contains('section') || cls.contains('ll-canvas-wrap'))) ||
        /^(section|article|figure|fieldset|li|td|th|details|summary|label|p|h1|h2|h3|h4|h5|h6)$/.test(tag)
      ) return node;
      var r = node.getBoundingClientRect();
      if (r.width >= 40 && r.height >= 24 && (node.children.length > 1 || String(node.innerText || '').trim().length > 20)) return node;
      node = node.parentElement;
    }
    return el;
  }

  function computeLocator(el) {
    if (el.id) return '#' + CSS.escape(el.id);
    var parts = [];
    var node = el;
    while (node && node.nodeType === 1 && parts.length < 6) {
      var part = node.tagName.toLowerCase();
      if (node.classList && node.classList.length) {
        part += '.' + Array.prototype.slice.call(node.classList).slice(0, 2).join('.');
      }
      var parent = node.parentElement;
      if (parent) {
        var siblings = Array.prototype.filter.call(parent.children, function (c) { return c.tagName === node.tagName; });
        if (siblings.length > 1) part += ':nth-of-type(' + (siblings.indexOf(node) + 1) + ')';
      }
      parts.unshift(part);
      if (node.id) break;
      node = parent;
    }
    return parts.join(' > ');
  }

  // ── Canvas instrumentation ─────────────────────────────────────────────
  // Patches CanvasRenderingContext2D so every draw call quietly logs what it
  // drew. No kit ever calls into this — it observes native canvas calls from
  // outside, so it works retroactively on every kit's canvas without a
  // single kit file changing. Purely observational: every patched method
  // still calls the real implementation with the real arguments and returns
  // its real result, so nothing about what actually renders can change.
  var CTX_PROTO = (typeof CanvasRenderingContext2D !== 'undefined') ? CanvasRenderingContext2D.prototype : null;
  var LOG_CAP = 2000, LOG_TRIM_TO = 1000;

  function safeTransform(ctx) {
    try {
      var m = ctx.getTransform();
      return { a: m.a, b: m.b, c: m.c, d: m.d, e: m.e, f: m.f };
    } catch (err) {
      return { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
    }
  }
  function applyM(m, x, y) { return { x: m.a * x + m.c * y + m.e, y: m.b * x + m.d * y + m.f }; }
  function scaleOfM(m) { return Math.sqrt(Math.abs(m.a * m.d - m.b * m.c)) || 1; }
  function bboxOfCorners(corners) {
    var xs = corners.map(function (c) { return c.x; });
    var ys = corners.map(function (c) { return c.y; });
    return { x0: Math.min.apply(null, xs), y0: Math.min.apply(null, ys), x1: Math.max.apply(null, xs), y1: Math.max.apply(null, ys) };
  }
  function pushEntry(ctx, entry) {
    var log = ctx.__llLog || (ctx.__llLog = []);
    entry.t = (window.performance && performance.now) ? performance.now() : Date.now();
    log.push(entry);
    if (log.length > LOG_CAP) log.splice(0, log.length - LOG_TRIM_TO);
  }
  // Styles are read live off ctx at paint time rather than shadow-tracked
  // through every fillStyle=/save()/restore() call — the context already IS
  // the live source of truth for its own current state, so this is both
  // simpler and immune to missing some obscure way a kit could mutate it.
  //
  // fillStyle/strokeStyle can be a CanvasGradient or CanvasPattern object
  // instead of a string (SimKit's own "glow circle" helper uses radial
  // gradients) — neither is structured-cloneable, so passing one straight
  // into a postMessage payload throws DataCloneError and silently drops the
  // WHOLE pick, not just that one field. Always coerce to a plain string.
  function safeColor(v) {
    return typeof v === 'string' ? v : (v ? '<gradient-or-pattern>' : v);
  }
  function liveStyle(ctx, painted) {
    return {
      fillStyle: painted !== 'stroke' ? safeColor(ctx.fillStyle) : undefined,
      strokeStyle: painted !== 'fill' ? safeColor(ctx.strokeStyle) : undefined,
      lineWidth: ctx.lineWidth,
      globalAlpha: ctx.globalAlpha,
      font: ctx.font,
    };
  }

  function installCanvasInstrumentation() {
    if (!CTX_PROTO || CTX_PROTO.__llPatched) return;
    CTX_PROTO.__llPatched = true;
    var orig = {};
    ['beginPath', 'moveTo', 'lineTo', 'rect', 'arc', 'ellipse', 'arcTo', 'bezierCurveTo', 'quadraticCurveTo',
      'fill', 'stroke', 'fillRect', 'strokeRect', 'clearRect', 'fillText', 'strokeText', 'drawImage']
      .forEach(function (name) { orig[name] = CTX_PROTO[name]; });

    function growPath(ctx, x, y) {
      var p = ctx.__llPath; if (!p) return;
      if (x < p.x0) p.x0 = x; if (y < p.y0) p.y0 = y;
      if (x > p.x1) p.x1 = x; if (y > p.y1) p.y1 = y;
      p.n++;
    }
    // A path painted by fill()/stroke() carries no memory of how it was
    // built by the time we can hit-test it, so the shape is reconstructed
    // incrementally as each path-building call comes in — cheaply, as just
    // a running bbox, EXCEPT the extremely common single-arc case (circles
    // are the majority of "things a learner points at" in these kits),
    // which is special-cased so a click on a circle resolves to an exact
    // center+radius instead of a loose box that also matches its corners.
    CTX_PROTO.beginPath = function () {
      this.__llPath = { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity, n: 0, circle: null, rectShape: null };
      return orig.beginPath.apply(this, arguments);
    };
    CTX_PROTO.moveTo = function (x, y) { growPath(this, x, y); return orig.moveTo.apply(this, arguments); };
    CTX_PROTO.lineTo = function (x, y) { growPath(this, x, y); return orig.lineTo.apply(this, arguments); };
    CTX_PROTO.rect = function (x, y, w, h) {
      growPath(this, x, y); growPath(this, x + w, y + h);
      if (this.__llPath && this.__llPath.n === 2) this.__llPath.rectShape = { x: x, y: y, w: w, h: h };
      return orig.rect.apply(this, arguments);
    };
    CTX_PROTO.arc = function (x, y, r) {
      growPath(this, x - r, y - r); growPath(this, x + r, y + r);
      if (this.__llPath && this.__llPath.n === 2) this.__llPath.circle = { x: x, y: y, r: r };
      return orig.arc.apply(this, arguments);
    };
    CTX_PROTO.ellipse = function (x, y, rx, ry) {
      growPath(this, x - rx, y - ry); growPath(this, x + rx, y + ry);
      if (this.__llPath && this.__llPath.n === 2) this.__llPath.circle = { x: x, y: y, r: Math.max(rx, ry) };
      return orig.ellipse.apply(this, arguments);
    };
    CTX_PROTO.arcTo = function (x1, y1, x2, y2) {
      growPath(this, x1, y1); growPath(this, x2, y2);
      return orig.arcTo.apply(this, arguments);
    };
    CTX_PROTO.bezierCurveTo = function (c1x, c1y, c2x, c2y, x, y) {
      growPath(this, c1x, c1y); growPath(this, c2x, c2y); growPath(this, x, y);
      return orig.bezierCurveTo.apply(this, arguments);
    };
    CTX_PROTO.quadraticCurveTo = function (cx, cy, x, y) {
      growPath(this, cx, cy); growPath(this, x, y);
      return orig.quadraticCurveTo.apply(this, arguments);
    };

    function logPathPaint(ctx, painted) {
      var p = ctx.__llPath;
      var m = safeTransform(ctx);
      if (p && p.circle && p.n === 2) {
        var c = applyM(m, p.circle.x, p.circle.y);
        var r = p.circle.r * scaleOfM(m);
        pushEntry(ctx, {
          kind: 'circle', painted: painted, userSpace: p.circle,
          circle: { dcx: c.x, dcy: c.y, dr: r },
          bbox: { x0: c.x - r, y0: c.y - r, x1: c.x + r, y1: c.y + r },
          style: liveStyle(ctx, painted),
        });
      } else if (p && isFinite(p.x0)) {
        var corners = [applyM(m, p.x0, p.y0), applyM(m, p.x1, p.y0), applyM(m, p.x0, p.y1), applyM(m, p.x1, p.y1)];
        pushEntry(ctx, {
          kind: p.rectShape ? 'rect' : 'path', painted: painted,
          userSpace: p.rectShape || { x0: p.x0, y0: p.y0, x1: p.x1, y1: p.y1 },
          bbox: bboxOfCorners(corners),
          style: liveStyle(ctx, painted),
        });
      }
    }
    // fill()/stroke() called with a Path2D/fill-rule argument aren't built
    // from THIS context's own path calls, so __llPath wouldn't describe
    // them — skip logging rather than record something misleading; the
    // pixel-color fallback still covers that click.
    CTX_PROTO.fill = function () {
      var res = orig.fill.apply(this, arguments);
      if (arguments.length === 0) logPathPaint(this, 'fill');
      return res;
    };
    CTX_PROTO.stroke = function () {
      var res = orig.stroke.apply(this, arguments);
      if (arguments.length === 0) logPathPaint(this, 'stroke');
      return res;
    };
    CTX_PROTO.fillRect = function (x, y, w, h) {
      var m = safeTransform(this);
      pushEntry(this, {
        kind: 'rect', painted: 'fill', userSpace: { x: x, y: y, w: w, h: h },
        bbox: bboxOfCorners([applyM(m, x, y), applyM(m, x + w, y), applyM(m, x, y + h), applyM(m, x + w, y + h)]),
        style: liveStyle(this, 'fill'),
      });
      return orig.fillRect.apply(this, arguments);
    };
    CTX_PROTO.strokeRect = function (x, y, w, h) {
      var m = safeTransform(this);
      pushEntry(this, {
        kind: 'rect', painted: 'stroke', userSpace: { x: x, y: y, w: w, h: h },
        bbox: bboxOfCorners([applyM(m, x, y), applyM(m, x + w, y), applyM(m, x, y + h), applyM(m, x + w, y + h)]),
        style: liveStyle(this, 'stroke'),
      });
      return orig.strokeRect.apply(this, arguments);
    };
    // A full-canvas clearRect is the dominant "start of a new frame" idiom
    // in these kits' draw loops — treated as a signal to drop everything
    // logged so far, so an armed pick made mid-animation can't resolve to a
    // shape from several frames ago that's no longer actually on screen.
    CTX_PROTO.clearRect = function (x, y, w, h) {
      try {
        if (this.canvas && w * h >= 0.6 * this.canvas.width * this.canvas.height && this.__llLog) this.__llLog.length = 0;
      } catch (err) { /* best-effort heuristic only — never let it break the real clear */ }
      return orig.clearRect.apply(this, arguments);
    };
    CTX_PROTO.fillText = function (text, x, y) {
      var m = safeTransform(this);
      var p = applyM(m, x, y);
      var w = 0; try { w = this.measureText(text).width * scaleOfM(m); } catch (err) { /* no-op */ }
      var h = (parseFloat(this.font) || 16) * scaleOfM(m);
      pushEntry(this, {
        kind: 'text', painted: 'fill', text: String(text).slice(0, 80), userSpace: { x: x, y: y },
        bbox: { x0: p.x, y0: p.y - h, x1: p.x + w, y1: p.y + h * 0.3 },
        style: liveStyle(this, 'fill'),
      });
      return orig.fillText.apply(this, arguments);
    };
    CTX_PROTO.strokeText = function (text, x, y) {
      var m = safeTransform(this);
      var p = applyM(m, x, y);
      var w = 0; try { w = this.measureText(text).width * scaleOfM(m); } catch (err) { /* no-op */ }
      var h = (parseFloat(this.font) || 16) * scaleOfM(m);
      pushEntry(this, {
        kind: 'text', painted: 'stroke', text: String(text).slice(0, 80), userSpace: { x: x, y: y },
        bbox: { x0: p.x, y0: p.y - h, x1: p.x + w, y1: p.y + h * 0.3 },
        style: liveStyle(this, 'stroke'),
      });
      return orig.strokeText.apply(this, arguments);
    };
    CTX_PROTO.drawImage = function (img, x, y, w, h) {
      if (typeof w === 'number' && typeof h === 'number') {
        var m = safeTransform(this);
        pushEntry(this, {
          kind: 'image', painted: 'image', userSpace: { x: x, y: y, w: w, h: h },
          bbox: bboxOfCorners([applyM(m, x, y), applyM(m, x + w, y), applyM(m, x, y + h), applyM(m, x + w, y + h)]),
          style: {},
        });
      }
      return orig.drawImage.apply(this, arguments);
    };
  }
  installCanvasInstrumentation();

  // ── Tier-1 precision hooks: SimKit / SimKit3D ──────────────────────────
  // The generic monkey-patch above only ever sees raw primitives — an arc,
  // a path, some text — so it can't tell a "spring" from any other zigzag
  // line, and has nothing at all to say about a 3D scene beyond whatever
  // flat 2D shapes it happens to project to. SimKit and SimKit3D are shared
  // drawing/scene libraries several kits are built on (see functions/libs/
  // simKit.js and simKit3D.js) — patched once here, every kit built on top
  // inherits real semantic identity for free, with zero changes to any
  // kit's own file. hookGlobalWhenDefined covers both possible script
  // orders: the kit script already loaded by the time this runs, or it
  // loads later and assigns window.SimKit/SimKit3D afterwards.
  function hookGlobalWhenDefined(name, onReady) {
    if (typeof window[name] !== 'undefined') { try { onReady(window[name]); } catch (err) { /* no-op */ } return; }
    var value;
    try {
      Object.defineProperty(window, name, {
        configurable: true, enumerable: true,
        get: function () { return value; },
        set: function (v) { value = v; try { onReady(v); } catch (err) { /* no-op */ } },
      });
    } catch (err) { /* something else already made this non-configurable — no worse off than before */ }
  }

  function patchSimKitDraw(drawObj) {
    if (!drawObj || drawObj.__llPatched) return;
    drawObj.__llPatched = true;
    // Wrapping these (rather than relying only on the primitives they call
    // internally) turns "a path spanning roughly x:10-90, y:20-95" plus an
    // unrelated triangle into ONE clean "an arrow from (10,20) to (90,95)" —
    // and since vectorField() calls draw.arrow() internally through this
    // SAME shared object, wrapping arrow here covers every arrow a vector
    // field draws too, with no separate vectorField-specific code needed.
    function wrap(name, describe) {
      var orig = drawObj[name];
      if (typeof orig !== 'function') return;
      drawObj[name] = function () {
        var ctx = arguments[0];
        var res = orig.apply(this, arguments);
        try {
          var entry = describe(ctx, arguments);
          if (entry) pushEntry(ctx, entry);
        } catch (err) { /* semantic tagging is a bonus on top of Tier-2 — never let it break the real draw */ }
        return res;
      };
    }
    wrap('arrow', function (ctx, a) {
      var x1 = a[1], y1 = a[2], x2 = a[3], y2 = a[4], opts = a[5] || {};
      var m = safeTransform(ctx), p1 = applyM(m, x1, y1), p2 = applyM(m, x2, y2), pad = 10 * scaleOfM(m);
      var col = safeColor(opts.color || '#4da3ff');
      return {
        kind: 'arrow', painted: 'stroke', userSpace: { x1: x1, y1: y1, x2: x2, y2: y2 },
        bbox: { x0: Math.min(p1.x, p2.x) - pad, y0: Math.min(p1.y, p2.y) - pad, x1: Math.max(p1.x, p2.x) + pad, y1: Math.max(p1.y, p2.y) + pad },
        style: { fillStyle: col, strokeStyle: col, lineWidth: opts.width || 2, font: ctx.font },
      };
    });
    wrap('angleArc', function (ctx, a) {
      var cx = a[1], cy = a[2], radius = a[3], a0 = a[4], a1 = a[5], opts = a[6] || {};
      var m = safeTransform(ctx), c = applyM(m, cx, cy), r = radius * scaleOfM(m);
      return {
        kind: 'angleArc', painted: 'stroke', userSpace: { cx: cx, cy: cy, radius: radius, a0: a0, a1: a1 },
        bbox: { x0: c.x - r, y0: c.y - r, x1: c.x + r, y1: c.y + r },
        style: { strokeStyle: safeColor(opts.color || '#ffb04d'), lineWidth: opts.width || 2, font: ctx.font },
      };
    });
    wrap('dashedLine', function (ctx, a) {
      var x1 = a[1], y1 = a[2], x2 = a[3], y2 = a[4], opts = a[5] || {};
      var m = safeTransform(ctx), p1 = applyM(m, x1, y1), p2 = applyM(m, x2, y2), pad = 6 * scaleOfM(m);
      return {
        kind: 'dashedLine', painted: 'stroke', userSpace: { x1: x1, y1: y1, x2: x2, y2: y2 },
        bbox: { x0: Math.min(p1.x, p2.x) - pad, y0: Math.min(p1.y, p2.y) - pad, x1: Math.max(p1.x, p2.x) + pad, y1: Math.max(p1.y, p2.y) + pad },
        style: { strokeStyle: safeColor(opts.color || 'rgba(128,128,128,0.6)'), lineWidth: opts.width || 1.5, font: ctx.font },
      };
    });
    wrap('labelBox', function (ctx, a) {
      var x = a[1], y = a[2], text = a[3], opts = a[4] || {};
      var m = safeTransform(ctx), p = applyM(m, x, y);
      var font = opts.font || '12px system-ui, sans-serif';
      var w = 0;
      try { var prevFont = ctx.font; ctx.font = font; w = ctx.measureText(text).width * scaleOfM(m); ctx.font = prevFont; } catch (err) { /* no-op */ }
      var padX = 10 * scaleOfM(m), hh = 12 * scaleOfM(m);
      return {
        kind: 'label', painted: 'fill', text: String(text).slice(0, 80), userSpace: { x: x, y: y },
        bbox: { x0: p.x - w / 2 - padX, y0: p.y - hh, x1: p.x + w / 2 + padX, y1: p.y + hh },
        style: { font: font },
      };
    });
    wrap('glowCircle', function (ctx, a) {
      var x = a[1], y = a[2], radius = a[3], color = a[4];
      var m = safeTransform(ctx), c = applyM(m, x, y), r = radius * scaleOfM(m);
      return {
        kind: 'circle', painted: 'fill', userSpace: { x: x, y: y, r: radius },
        circle: { dcx: c.x, dcy: c.y, dr: r },
        bbox: { x0: c.x - r, y0: c.y - r, x1: c.x + r, y1: c.y + r },
        style: { fillStyle: safeColor(color) },
      };
    });
    wrap('spring', function (ctx, a) {
      var x1 = a[1], y1 = a[2], x2 = a[3], y2 = a[4], opts = a[5] || {};
      var m = safeTransform(ctx), p1 = applyM(m, x1, y1), p2 = applyM(m, x2, y2), pad = (opts.amp || 8) * scaleOfM(m) + 4;
      return {
        kind: 'spring', painted: 'stroke', userSpace: { x1: x1, y1: y1, x2: x2, y2: y2 },
        bbox: { x0: Math.min(p1.x, p2.x) - pad, y0: Math.min(p1.y, p2.y) - pad, x1: Math.max(p1.x, p2.x) + pad, y1: Math.max(p1.y, p2.y) + pad },
        style: { strokeStyle: safeColor(opts.color || '#9aa7b8'), lineWidth: opts.width || 2, font: ctx.font },
      };
    });
    wrap('rod', function (ctx, a) {
      var x1 = a[1], y1 = a[2], x2 = a[3], y2 = a[4], opts = a[5] || {};
      var m = safeTransform(ctx), p1 = applyM(m, x1, y1), p2 = applyM(m, x2, y2), pad = ((opts.width || 4) / 2 + 2) * scaleOfM(m);
      return {
        kind: 'rod', painted: 'stroke', userSpace: { x1: x1, y1: y1, x2: x2, y2: y2 },
        bbox: { x0: Math.min(p1.x, p2.x) - pad, y0: Math.min(p1.y, p2.y) - pad, x1: Math.max(p1.x, p2.x) + pad, y1: Math.max(p1.y, p2.y) + pad },
        style: { strokeStyle: safeColor(opts.color || '#9aa7b8'), lineWidth: opts.width || 4, font: ctx.font },
      };
    });
  }

  // Mirrors simKit3D.js's own (closure-private, not exposed on window.SimKit3D)
  // transformPoint/rotateEuler/scaleXYZ exactly — position/rotation/scale,
  // including the {x,y,z} non-uniform-scale form — so pickAt's projection
  // matches what actually got rendered. Duplicated rather than reached into
  // internally since those helpers aren't part of the public API surface.
  function transformPoint3D(p, obj) {
    var s = obj.scale, sx, sy, sz;
    if (s === undefined) { sx = sy = sz = 1; } else if (typeof s === 'object' && s !== null) {
      sx = s.x === undefined ? 1 : s.x; sy = s.y === undefined ? 1 : s.y; sz = s.z === undefined ? 1 : s.z;
    } else { sx = sy = sz = s; }
    var x = p.x * sx, y = p.y * sy, z = p.z * sz;
    var rot = obj.rotation;
    if (rot) {
      var c, sn, t;
      if (rot.x) { c = Math.cos(rot.x); sn = Math.sin(rot.x); t = y; y = c * y - sn * z; z = sn * t + c * z; }
      if (rot.y) { c = Math.cos(rot.y); sn = Math.sin(rot.y); t = x; x = c * x + sn * z; z = -sn * t + c * z; }
      if (rot.z) { c = Math.cos(rot.z); sn = Math.sin(rot.z); t = x; x = c * x - sn * y; y = sn * t + c * y; }
    }
    if (obj.position) { x += obj.position.x; y += obj.position.y; z += obj.position.z; }
    return { x: x, y: y, z: z };
  }

  function patchSimKit3D(S3D) {
    if (!S3D || !S3D.Scene || S3D.Scene.__llPatched) return;
    S3D.Scene.__llPatched = true;
    // render() is called every frame (or once, for a static scene) with the
    // (canvas, camera) pair currently in use — there's no other way to
    // discover from outside which Scene/camera a given <canvas> belongs to,
    // since kits create and hold those themselves. Stamping it on the
    // canvas element each call means it's always known by click time.
    var origRender = S3D.Scene.prototype.render;
    if (typeof origRender === 'function') {
      S3D.Scene.prototype.render = function (ctx, camera) {
        try { if (ctx && ctx.canvas) ctx.canvas.__llLastScene3D = { scene: this, camera: camera }; } catch (err) { /* no-op */ }
        return origRender.apply(this, arguments);
      };
    }
    // Real hit-test against the actual object model (kind/position/color/
    // scale), not inferred from 2D pixels — reuses the same projection
    // basis/focal/near-cull as Scene.prototype.render, kept as a parallel
    // implementation rather than a refactor of render itself so this
    // addition can't change a single pixel of what actually renders.
    S3D.Scene.prototype.pickAt = function (ctx, camera, px, py) {
      var canvas = ctx.canvas;
      var basis = camera.basis();
      var focal = (canvas.height / 2) / Math.tan(camera.fov / 2);
      var cx = canvas.width / 2, cy = canvas.height / 2, near = camera.near;
      function proj(c) { var s = focal / c.z; return { x: cx + c.x * s, y: cy - c.y * s }; }
      var hits = [];
      for (var oi = 0; oi < this.objects.length; oi++) {
        var obj = this.objects[oi];
        if (obj.visible === false) continue;
        var srcPts = obj.vertices || obj.pts || (obj.position ? [obj.position] : null);
        if (!srcPts || !srcPts.length) continue;
        var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity, zsum = 0, n = 0;
        for (var i = 0; i < srcPts.length; i++) {
          var wc = obj.noTransform ? srcPts[i] : transformPoint3D(srcPts[i], obj);
          var cc = camera.toCamera(wc, basis);
          if (cc.z <= near) continue;
          var sp = proj(cc);
          if (sp.x < minX) minX = sp.x; if (sp.y < minY) minY = sp.y;
          if (sp.x > maxX) maxX = sp.x; if (sp.y > maxY) maxY = sp.y;
          zsum += cc.z; n++;
        }
        if (!n) continue;
        var pad = srcPts.length === 1 ? 14 : 4; // single-point objects (sprite/anchor) get a generous pick radius
        if (px >= minX - pad && px <= maxX + pad && py >= minY - pad && py <= maxY + pad) {
          hits.push({
            kind: obj.kind || 'mesh', name: obj.name || null, color: obj.color || null,
            position: obj.position || null, scale: obj.scale === undefined ? null : obj.scale,
            vertexCount: srcPts.length, depth: zsum / n,
          });
        }
      }
      hits.sort(function (a, b) { return a.depth - b.depth; });
      return hits.slice(0, 6);
    };
    // mesh.box/sphere/... only ever copy a fixed set of known fields onto
    // the object they return — any extra opts field (like a kit author's
    // own opts.name) is silently dropped today. Preserving it costs nothing
    // when nobody passes one, and means a future pick can report a real
    // name instead of only geometry once kit-authored code starts using it.
    if (S3D.mesh) {
      ['box', 'sphere', 'cylinder', 'cone', 'torus', 'plane', 'cylinderBetween'].forEach(function (name) {
        var orig = S3D.mesh[name];
        if (typeof orig !== 'function') return;
        S3D.mesh[name] = function (opts) {
          var res = orig.apply(this, arguments);
          if (res && opts && (opts.name || opts.label)) res.name = opts.name || opts.label;
          return res;
        };
      });
    }
  }
  hookGlobalWhenDefined('SimKit', function (v) { if (v && v.draw) patchSimKitDraw(v.draw); });
  hookGlobalWhenDefined('SimKit3D', patchSimKit3D);

  // Resolves a click at device-pixel (px,py) on canvasEl against whatever
  // installCanvasInstrumentation has logged for it. Walked newest-first so
  // overlapping shapes resolve to whatever is currently on top/current, and
  // capped so an unlucky click over a dense pile of shapes doesn't hand the
  // model an unreadable wall of matches.
  function resolveCanvasHit(canvasEl, px, py) {
    var ctx = null;
    try { ctx = canvasEl.getContext('2d'); } catch (err) { ctx = null; }
    var log = (ctx && ctx.__llLog) || [];
    var TOL = 3, TEXT_NEAR = 40;
    var matches = [], nearbyText = [];
    for (var i = log.length - 1; i >= 0 && matches.length < 6; i--) {
      var e = log[i];
      var hit = false;
      if (e.circle) {
        var dx = px - e.circle.dcx, dy = py - e.circle.dcy;
        hit = Math.sqrt(dx * dx + dy * dy) <= (e.circle.dr + TOL);
      } else if (e.bbox) {
        hit = px >= e.bbox.x0 - TOL && px <= e.bbox.x1 + TOL && py >= e.bbox.y0 - TOL && py <= e.bbox.y1 + TOL;
      }
      if (hit) {
        matches.push(e);
      } else if (e.kind === 'text' && e.bbox && nearbyText.length < 4) {
        var tcx = (e.bbox.x0 + e.bbox.x1) / 2, tcy = (e.bbox.y0 + e.bbox.y1) / 2;
        if (Math.sqrt((px - tcx) * (px - tcx) + (py - tcy) * (py - tcy)) <= TEXT_NEAR) nearbyText.push(e);
      }
    }
    var pixelColor = null;
    if (ctx) {
      try {
        var d = ctx.getImageData(px, py, 1, 1).data;
        pixelColor = 'rgba(' + d[0] + ',' + d[1] + ',' + d[2] + ',' + (d[3] / 255).toFixed(2) + ')';
      } catch (err) { /* tainted or unreadable — pixelColor stays null */ }
    }
    return { matches: matches, nearbyText: nearbyText, pixelColor: pixelColor };
  }

  window.addEventListener('message', function (event) {
    var d = event.data;
    if (!d) return;
    if (d.__llPickerArm === true) {
      armed = true;
      document.body && (document.body.style.cursor = 'crosshair');
    } else if (d.__llPickerDisarm === true) {
      armed = false;
      document.body && (document.body.style.cursor = '');
      hoverBox.style.display = 'none';
    }
  });

  document.addEventListener('mouseover', function (e) {
    if (!armed || !e.target || e.target === hoverBox || e.target.closest('[data-ll-bridge-ui]')) return;
    var subject = choosePickElement(e.target);
    if (!subject) return;
    var r = subject.getBoundingClientRect();
    hoverBox.style.left = r.left + 'px';
    hoverBox.style.top = r.top + 'px';
    hoverBox.style.width = r.width + 'px';
    hoverBox.style.height = r.height + 'px';
    hoverBox.style.display = 'block';
  }, true);

  function showHoverAt(clientX, clientY) {
    var subject = choosePickElement(document.elementFromPoint(clientX, clientY));
    if (!subject) {
      hoverBox.style.display = 'none';
      return;
    }
    var r = subject.getBoundingClientRect();
    hoverBox.style.left = r.left + 'px';
    hoverBox.style.top = r.top + 'px';
    hoverBox.style.width = r.width + 'px';
    hoverBox.style.height = r.height + 'px';
    hoverBox.style.display = 'block';
  }

  function pickAt(clientX, clientY, target) {
    var el = choosePickElement(target || document.elementFromPoint(clientX, clientY));
    if (!el) return;
    var r = el.getBoundingClientRect();
    var label = describeElement(el);
    // A <canvas> has no DOM inside it to point at — everything on it is
    // pixel-drawn, so a locator for the element itself only ever resolves
    // to the whole canvas. Where inside it the learner clicked is the only
    // signal for WHICH drawn thing they meant, so capture it as a fraction
    // of the canvas's own size (not raw pixels — those are meaningless
    // without the canvas's dimensions, which the model doesn't have here).
    var canvasPoint = null;
    var canvasHit = null;
    if ((el.tagName || '').toLowerCase() === 'canvas' && r.width > 0 && r.height > 0) {
      var xFrac = (clientX - r.left) / r.width;
      var yFrac = (clientY - r.top) / r.height;
      canvasPoint = { x: xFrac, y: yFrac };
      label += ' — clicked at ' + Math.round(xFrac * 100) + '% across, ' + Math.round(yFrac * 100) + '% down';
      // Fraction -> the canvas's own backing-buffer pixels (el.width/height,
      // NOT the CSS-displayed r.width/height) — that's the coordinate space
      // every logged shape's bbox was recorded in.
      var px = xFrac * (el.width || r.width);
      var py = yFrac * (el.height || r.height);
      canvasHit = resolveCanvasHit(el, px, py);
      canvasHit.pixel = { x: Math.round(px), y: Math.round(py) };
      if (el.__llLastScene3D) {
        try {
          var s3 = el.__llLastScene3D;
          canvasHit.scene3D = s3.scene.pickAt(el.getContext('2d'), s3.camera, px, py);
        } catch (err) { /* no-op — 2D canvasHit above still stands */ }
      }
    }
    parent.postMessage({
      __llPickerPick: true,
      locator: computeLocator(el),
      label: label,
      rect: { left: r.left, top: r.top, width: r.width, height: r.height },
      // Exact click point (viewport coords, same space as rect) — used in
      // place of the element's bounding-box center so the pin marker lands
      // where the learner actually clicked, not just at the top of
      // whatever they clicked on. Matters most for a large canvas.
      point: { left: clientX, top: clientY },
      canvasPoint: canvasPoint,
      canvasHit: canvasHit
    }, '*');
  }

  document.addEventListener('click', function (e) {
    if (!armed) return;
    if (e.target && e.target.closest('[data-ll-bridge-ui]')) return;
    e.preventDefault();
    e.stopPropagation();
    pickAt(e.clientX, e.clientY, e.target);
  }, true);

  window.addEventListener('message', function (event) {
    var d = event.data;
    if (!d || d.__llPickerPickAt !== true) return;
    pickAt(Number(d.x) || 0, Number(d.y) || 0, null);
  });

  window.addEventListener('message', function (event) {
    var d = event.data;
    if (!d) return;
    if (d.__llPickerHoverAt === true) {
      showHoverAt(Number(d.x) || 0, Number(d.y) || 0);
    } else if (d.__llPickerHoverClear === true) {
      hoverBox.style.display = 'none';
    }
  });
})();
</script>`;

// Injects the picker script right before </head>, same insertion style as
// injectAskAIBridge — falls back to leaving the HTML untouched if there's
// no </head> to anchor on. Safe to combine with injectAskAIBridge (call
// this on the result of that, in either order) since each targets whatever
// single </head> remains after the other has already run.
export function injectElementPickerBridge(html) {
  const source = String(html || '');
  if (source.includes(PICKER_SCRIPT_MARKER)) return source;
  if (/<\/head>/i.test(source)) return source.replace(/<\/head>/i, `${PICKER_SCRIPT}</head>`);
  if (/<html[^>]*>/i.test(source)) return source.replace(/<html[^>]*>/i, (m) => `${m}<head>${PICKER_SCRIPT}</head>`);
  return `${PICKER_SCRIPT}${source}`;
}

// Parent-side listener for one live iframe. Call onPick({ locator, label,
// rect }) every time the learner clicks something while armed. Returns a
// detach function — call it before attaching to a replacement iframe.
const pickerBridgeStates = new WeakMap();

export function attachElementPickerBridge(iframeEl, { onPick }) {
  pickerBridgeStates.set(iframeEl, { onPick, pending: null });
  function onMessage(event) {
    const d = event.data;
    if (!d || d.__llPickerPick !== true || event.source !== iframeEl.contentWindow) return;
    const state = pickerBridgeStates.get(iframeEl);
    if (state?.pending) {
      clearTimeout(state.pending.timer);
      if (state.pending.fired) {
        state.pending = null;
        return;
      }
      state.pending = null;
    }
    onPick({
      locator: d.locator, label: d.label, rect: d.rect, point: d.point, canvasPoint: d.canvasPoint, canvasHit: d.canvasHit,
    });
  }
  window.addEventListener('message', onMessage);
  return () => {
    const state = pickerBridgeStates.get(iframeEl);
    if (state?.pending) clearTimeout(state.pending.timer);
    pickerBridgeStates.delete(iframeEl);
    window.removeEventListener('message', onMessage);
  };
}

// Arms/disarms picking mode inside a given iframe. No-ops harmlessly if the
// iframe has no content window yet (e.g. mid-swap).
const pickerOverlays = new WeakMap();

function removePickerOverlay(iframeEl) {
  const state = iframeEl ? pickerOverlays.get(iframeEl) : null;
  if (!state) return;
  state.cleanup();
  state.overlay.remove();
  pickerOverlays.delete(iframeEl);
}

function ensurePickerOverlay(iframeEl) {
  if (!iframeEl || pickerOverlays.has(iframeEl)) return;
  const overlay = document.createElement('div');
  overlay.setAttribute('data-ll-parent-picker-overlay', 'true');
  overlay.style.cssText = 'position:fixed;z-index:2147483000;background:rgba(204,120,92,0.03);cursor:crosshair;';
  const update = () => {
    if (!iframeEl.isConnected) {
      removePickerOverlay(iframeEl);
      return;
    }
    const r = iframeEl.getBoundingClientRect();
    overlay.style.left = `${r.left}px`;
    overlay.style.top = `${r.top}px`;
    overlay.style.width = `${r.width}px`;
    overlay.style.height = `${r.height}px`;
    overlay.style.display = r.width > 0 && r.height > 0 ? 'block' : 'none';
  };
  const sendPickAt = (event) => {
    update();
    const r = iframeEl.getBoundingClientRect();
    const x = event.clientX - r.left;
    const y = event.clientY - r.top;
    const state = pickerBridgeStates.get(iframeEl);
    if (state) {
      if (state.pending) clearTimeout(state.pending.timer);
      const pending = {
        timer: setTimeout(() => {
          state.pending = null;
        }, 1500),
      };
      state.pending = pending;
    }
    iframeEl.contentWindow?.postMessage({
      __llPickerPickAt: true,
      x,
      y,
    }, '*');
  };
  let lastHoverAt = 0;
  const sendHoverAt = (event) => {
    const now = performance.now();
    if (now - lastHoverAt < 32) return;
    lastHoverAt = now;
    update();
    const r = iframeEl.getBoundingClientRect();
    iframeEl.contentWindow?.postMessage({
      __llPickerHoverAt: true,
      x: event.clientX - r.left,
      y: event.clientY - r.top,
    }, '*');
  };
  overlay.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    sendPickAt(event);
  });
  overlay.addEventListener('mousemove', sendHoverAt);
  overlay.addEventListener('mouseleave', () => {
    iframeEl.contentWindow?.postMessage({ __llPickerHoverClear: true }, '*');
  });
  window.addEventListener('resize', update);
  window.addEventListener('scroll', update, true);
  document.body.appendChild(overlay);
  update();
  pickerOverlays.set(iframeEl, {
    overlay,
    cleanup: () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    },
  });
}

export function armPicker(iframeEl) {
  const win = iframeEl?.contentWindow;
  if (!win) return;
  ensurePickerOverlay(iframeEl);
  const send = () => win.postMessage({ __llPickerArm: true }, '*');
  send();
  [80, 250, 600].forEach((ms) => setTimeout(send, ms));
}
export function disarmPicker(iframeEl) {
  removePickerOverlay(iframeEl);
  iframeEl?.contentWindow?.postMessage({ __llPickerDisarm: true }, '*');
}
